import React, { useState } from 'react';
import { extractUtmParams, submitFunnelBooking } from '../../../lib/funnelPublicApi';

const inputClass =
  'w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent';

export default function BookingBlock({
  content = {},
  blockId,
  funnelId,
  stepId,
  funnelSlug,
}) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    scheduled_at: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('All fields are required.');
      return;
    }
    if (!form.scheduled_at) {
      setError('Please select a preferred date and time.');
      return;
    }

    setLoading(true);
    try {
      const utm = extractUtmParams(new URLSearchParams(window.location.search));
      await submitFunnelBooking({
        funnel_id: funnelId,
        step_id: stepId,
        block_id: blockId,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        assignment_mode: content.assignment_mode || 'round_robin',
        assigned_user_id: content.assigned_user_id || null,
        ...utm,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section id="funnel-booking-block" className="py-8 px-4 sm:px-0">
        <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center">
          <p className="text-green-800 font-semibold text-lg mb-1">You&apos;re booked!</p>
          <p className="text-green-700 text-sm">
            We&apos;ve received your appointment request and will be in touch shortly.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="funnel-booking-block" className="py-8 px-4 sm:px-0">
      {content.title && (
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2">
          {content.title}
        </h2>
      )}
      {content.subtitle && (
        <p className="text-gray-600 text-sm text-center mb-6">{content.subtitle}</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 max-w-lg mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="booking-first-name" className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              id="booking-first-name"
              type="text"
              required
              value={form.first_name}
              onChange={setField('first_name')}
              className={inputClass}
              autoComplete="given-name"
            />
          </div>
          <div>
            <label htmlFor="booking-last-name" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              id="booking-last-name"
              type="text"
              required
              value={form.last_name}
              onChange={setField('last_name')}
              className={inputClass}
              autoComplete="family-name"
            />
          </div>
        </div>
        <div>
          <label htmlFor="booking-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            id="booking-email"
            type="email"
            required
            value={form.email}
            onChange={setField('email')}
            className={inputClass}
            autoComplete="email"
          />
        </div>
        <div>
          <label htmlFor="booking-phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            id="booking-phone"
            type="tel"
            required
            value={form.phone}
            onChange={setField('phone')}
            className={inputClass}
            autoComplete="tel"
          />
        </div>
        <div>
          <label htmlFor="booking-datetime" className="block text-sm font-medium text-gray-700 mb-1">
            Preferred Date &amp; Time
          </label>
          <input
            id="booking-datetime"
            type="datetime-local"
            required
            value={form.scheduled_at}
            onChange={setField('scheduled_at')}
            className={inputClass}
          />
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 rounded-lg text-white font-semibold text-base transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ backgroundColor: '#00AAFF' }}
        >
          {loading ? 'Booking…' : 'Confirm Appointment'}
        </button>
      </form>
    </section>
  );
}
